/**
 * @fileoverview Zod schema and types for the weave snapshot.
 *
 * A weave snapshot captures continuity-focused analysis for a chapter,
 * synthesizing storyline activity and arc progression. It is generated
 * after plot, character, and plot thread snapshots complete (Stage 2 of
 * the snapshot pipeline).
 *
 * Plot thread lifecycle management has been moved to the dedicated
 * plot-thread-snapshotter-v1 agent.
 */

import { z } from 'zod';

// =============================================================================
// WEAVE SNAPSHOT SCHEMA
// =============================================================================

export const WeaveSnapshotSchema = z.object({
  id: z.string().describe('Unique identifier (same as chapterId for idempotency).'),
  chapterId: z.string().describe('The chapter this weave snapshot belongs to.'),
  timestamp: z.string().describe('ISO timestamp when created.'),

  storylineActivity: z.array(z.object({
    storylineId: z.string(),
    tensionLevel: z.enum(['low', 'rising', 'high', 'release']),
    evidence: z.string(),
  })).describe('Which storylines are active in this chapter with tension levels.'),

  storylineQuestionUpdates: z.array(z.object({
    storylineId: z.string(),
    questionId: z.string(),
    status: z.enum(['open', 'answered']),
    confidence: z.number().min(0).max(1),
    evidence: z.string(),
  })).describe('Story question status changes detected in this chapter.'),

  storylineBeatTouches: z.array(z.object({
    storylineId: z.string(),
    beatId: z.string(),
    touchType: z.enum(['foreshadowed', 'advanced', 'fulfilled']),
    confidence: z.number().min(0).max(1),
    evidence: z.string(),
  })).describe('Storyline beats touched in this chapter.'),

  characterArcTouches: z.array(z.object({
    characterId: z.string(),
    arcBeatId: z.string().optional(),
    structurePhase: z.string().optional(),
    touchType: z.enum(['foreshadowed', 'advanced', 'fulfilled']),
    confidence: z.number().min(0).max(1),
    evidence: z.string(),
  })).optional().describe('Character arc beats touched in this chapter (long-form only).'),

  characterArcProgress: z.array(z.object({
    characterId: z.string(),
    arcName: z.string(),
    arcSummary: z.string(),
    progressNote: z.string(),
    evidence: z.string(),
  })).optional().describe('Simplified character arc progress observations (short-form only).'),
});

export type WeaveSnapshot = z.infer<typeof WeaveSnapshotSchema>;
